import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  EmptyStation,
  type FormActionData,
  MessageBar,
  ReadOnlyField,
  type StationMessage,
  TextButton,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ResetIdpUserPasswordDocument,
  type ResetIdpUserPasswordMutation,
  type ResetIdpUserPasswordMutationVariables,
} from '../../../../generated/axauth-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './UserResetPassword.module.scss';

const defaultPassword = '*********************************';

export const UserResetPassword: React.FC = () => {
  const { envId, userId } = useParams<{
    envId: string;
    userId: string;
  }>();

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<StationMessage>();
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of axauth-service
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );
        setServiceStatusMessage(statusMessage);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId]);

  const { actions, newPassword } = useGenerationActions(userId, envId);

  const Form: React.FC<{
    newPassword: string;
  }> = ({ newPassword }) => {
    const onCopyClickedHandler = (): void => {
      try {
        // copy secret to clipboard
        navigator.clipboard.writeText(newPassword ?? '');
      } catch (_error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to copy to clipboard');
      }
    };

    return (
      <>
        <div
          className={clsx(classes.password, {
            [classes.hasWarning]: newPassword !== defaultPassword,
          })}
        >
          <ReadOnlyField value={newPassword} label="Password" />
          {newPassword !== defaultPassword && (
            <>
              <TextButton
                className={classes.button}
                text="Copy"
                onButtonClicked={onCopyClickedHandler}
              />
            </>
          )}
        </div>
      </>
    );
  };

  useLayoutEffect(() => {
    if (newPassword !== defaultPassword) {
      setMessage({
        title:
          'WARNING: Please copy the password displayed below and store it in a secure location. Once this station is closed, the password cannot be retrieved again and can only be replaced by generating a new password.',
        type: 'warning',
      });
    }
  }, [newPassword]);

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Reset User Password">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details
          defaultTitle="Reset User Password"
          actions={actions}
          initialData={{
            loading,
          }}
          saveData={() => Promise.resolve()}
          stationMessage={message}
        >
          <Form newPassword={newPassword} />
        </Details>
      )}
    </>
  );
};

const useGenerationActions = (
  userId: string,
  envId: string,
): {
  readonly actions: FormActionData[];
  readonly newPassword: string;
} => {
  const [newPassword, setNewPassword] = useState<string>(defaultPassword);

  const generateNewPassword = async (): Promise<void> => {
    const envApolloClient = environmentApolloClient(
      envId,
      new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
    );

    const response = await envApolloClient?.mutate<
      ResetIdpUserPasswordMutation,
      ResetIdpUserPasswordMutationVariables
    >({
      mutation: ResetIdpUserPasswordDocument,
      variables: {
        input: {
          id: userId,
        },
      },
      fetchPolicy: 'no-cache',
    });

    setNewPassword(
      response.data?.resetIdpUserPassword.newPassword ?? defaultPassword,
    );
  };

  const actions: FormActionData[] = [];
  if (newPassword === defaultPassword) {
    actions.push({
      label: 'Generate New Password',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Are you sure you want to reset the password?',
      },
      onActionSelected: async () => {
        return generateNewPassword();
      },
    });
  }

  return {
    actions,
    newPassword,
  } as const;
};

export const UserResetPasswordCrumb: BreadcrumbResolver = () =>
  'Reset Password';
